import { Injectable } from '@angular/core';
import {
  createFrom,
  EventService,
  FeatureConfigService,
  ProductSearchService,
  ProductService
} from '@spartacus/core';
import { NavigationEvent, ProductPageEventBuilder } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import { filter, map, skip, switchMap, take, tap } from 'rxjs/operators';
import { TagManagerFeatureService } from '../tag-manager-feature.service';
import {
  CategoryPageResultsEvent,
  CustomProductDetailsPageEvent,
  CustomProductImpressionResultsEvent,
  CustomProductSelectedEvent,
  SearchPageResultsEvent
} from './custom-product-page.events';
import { CustomCleanEcommerceEvent } from "../common/custom-common.event";

@Injectable({
  providedIn: 'root',
})
export class CustomProductPageEventBuilder extends ProductPageEventBuilder {
  constructor(
    protected eventService: EventService,
    protected productService: ProductService,
    protected productSearchService: ProductSearchService,
    // TODO: #10896 - remove this
    /** @deprecated @since 3.1 - this will be removed in 4.0 */
    protected featureConfigService?: FeatureConfigService,
    protected tagManagerFeatureService?: TagManagerFeatureService,
  ) {
    super(eventService, productService, productSearchService);
    this.customRegister();
  }

  protected customRegister(): void {
    this.eventService.register(
      SearchPageResultsEvent,
      this.buildSearchPageResultsEvent()
    );
    this.eventService.register(
      CustomProductDetailsPageEvent,
      this.customBuildProductDetailsPageEvent()
    );
    this.eventService.register(
      CategoryPageResultsEvent,
      this.buildCategoryResultsPageEvent()
    );

    // Impression event in category and search page (view_item_list)
    this.eventService.register(
      CustomProductImpressionResultsEvent,
      this.buildProductListImpressionEvent("category")
    );

    this.eventService.register(
      CustomProductImpressionResultsEvent,
      this.buildProductSelectedEvent()
    );
  }

  /**
   * Builds the navigation events
   */
  protected customBuildProductDetailsPageEvent(): Observable<CustomProductDetailsPageEvent> {
    return this.eventService.get(NavigationEvent).pipe(
      filter((navigationEvent) => navigationEvent.semanticRoute === 'product'),
      switchMap((navigationEvent) =>
        this.productService.get(navigationEvent.context.id).pipe(
          filter((product) => Boolean(product)),
          take(1),
          tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
          map((product) =>
            createFrom(CustomProductDetailsPageEvent, {
              event: 'detail',
              ecommerce: {
                currency: product.price.currencyIso,
                value: product.price.value,
                items: [
                  this.tagManagerFeatureService.getEcommerceProductDetailItem(product),
                ]
              },
            })
          )
        )
      )
    );
  }

  protected buildProductListImpressionEvent(semanticRoute: string): Observable<CustomProductImpressionResultsEvent> {
    const searchResults$ = this.productSearchService.getResults().pipe(skip(1));
    return this.eventService.get(NavigationEvent).pipe(
      filter(navigationEvent => navigationEvent.semanticRoute === semanticRoute),
      switchMap((navigationEvent) => {
        return searchResults$.pipe(
          filter(searchResult => (searchResult && (Object.keys(searchResult).length > 0))),
          take(1),
          tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
          map(searchResults => {
            const _items = searchResults.products?.map((product, index) =>
              this.tagManagerFeatureService.getProductImpressionItem(product, index)
            ) ?? [];

            return createFrom(CustomProductImpressionResultsEvent, {
              event: 'view_item_list',
              ecommerce: {
                item_list_id: navigationEvent?.context?.id,
                item_list_name: searchResults.breadcrumbs?.[0]?.facetValueName || '',
                items: _items
              }
            });
          })
        );
      })
    );
  }

  protected buildProductSelectedEvent(): Observable<CustomProductImpressionResultsEvent> {
    return this.eventService.get(CustomProductSelectedEvent).pipe(
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map(selectedItem => {
          const _item = this.tagManagerFeatureService.getProductSelectedItem(selectedItem.product);
          const _activateRouteParams = selectedItem.activateRouteParams;
          return createFrom(CustomProductImpressionResultsEvent, {
            event: 'select_item',
            ecommerce: {
              item_list_id: _activateRouteParams.categoryCode ?? "",
              item_list_name: _activateRouteParams.categoryName ?? "",
              items: [_item]
            }
          })
        }
      )
    )
  }
}
